// src/components/Header.jsx

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';

import { Link, useNavigate } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';

import { logout } from '../../../store/authSlice.js';
import { openAuth } from '../../../store/uiSlice.js';

import { useToast } from '../../common/Toast/Toast.jsx';

import {
  ShoppingBag,
  User,
  Heart,
  Phone,
  Search,
  X,
  Globe,
  ChevronDown,
  ArrowRight,
  Menu,
} from 'lucide-react';

import { ThemeToggle } from '../../ui/ThemeToggle/ThemeToggle.jsx';

import logo_hor from '../../../assets/logo_hor.svg';

import { menuAPI } from '../../../api/menu.js';


const PLACEHOLDER_IMAGE =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200"%3E%3Crect width="300" height="200" fill="%23F0F0F0"/%3E%3Ctext x="150" y="100" font-family="Arial" font-size="20" fill="%23999999" text-anchor="middle"%3E%D0%98%D0%B7%D0%BE%D0%B1%D1%80%D0%B0%D0%B6%D0%B5%D0%BD%D0%B8%D0%B5%3C/text%3E%3Ctext x="150" y="130" font-family="Arial" font-size="14" fill="%23BBBBBB" text-anchor="middle"%3E%D0%B2%20%D1%80%D0%B0%D0%B7%D1%80%D0%B0%D0%B1%D0%BE%D1%82%D0%BA%D0%B5%3C/text%3E%3C/svg%3E';


const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [activeMegaMenu, setActiveMegaMenu] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');

  const [menuItems, setMenuItems] = useState([]);

  const [megaMenuData, setMegaMenuData] = useState(null);

  const [isLoading, setIsLoading] = useState(true);

  const [showGeoBar, setShowGeoBar] = useState(true);

  const [showLanguageBar, setShowLanguageBar] = useState(true);

  const [showPromoBar, setShowPromoBar] = useState(true);


  const dispatch = useDispatch();

  const navigate = useNavigate();

  const toast = useToast();


  const menuTimeoutRef = useRef(null);


  const isUserAuthenticated = useSelector(
    (state) => state.auth.isUserAuthenticated
  );

  const user = useSelector(
    (state) => state.auth.user
  );


  // =========================================================
  // LOAD MENU
  // =========================================================

  const loadMenu = useCallback(async () => {
    try {
      setIsLoading(true);

      const response = await menuAPI.getMenu();

      const items = response?.data?.data || [];

      const adaptedItems = items.map((item) => ({
        ...item,

        label:
          item.name ||
          item.label ||
          'Без названия',

        hasMegaMenu: true,

        link:
          `/catalog/${item.slug || ''}`,
      }));

      setMenuItems(adaptedItems);
    } catch (error) {
      console.error(
        'Error loading menu:',
        error
      );

      setMenuItems([]);
    } finally {
      setIsLoading(false);
    }
  }, []);


  const loadMegaMenu = useCallback(
    async (categoryId) => {
      if (!categoryId) return;

      try {
        const response =
          await menuAPI.getMegaMenu(categoryId);

        const data =
          response?.data?.data;

        if (!data) {
          setMegaMenuData(null);
          return;
        }


        const children =
          data.children || [];


        const columns =
          children.map((child) => ({
            title:
              child?.name ||
              child?.label ||
              'Категория',

            links:
              (
                child?.Products ||
                child?.products ||
                []
              ).map((product) => ({
                label:
                  product?.name ||
                  'Товар',

                link:
                  `/product/${product?.id || ''}`,
              })),
          }));


        const categoryProducts =
          data?.Products ||
          data?.products ||
          [];


        if (
          categoryProducts.length > 0
        ) {
          columns.push({
            title:
              data?.name ||
              'Все товары',

            links:
              categoryProducts.map(
                (product) => ({
                  label:
                    product?.name ||
                    'Товар',

                  link:
                    `/product/${product?.id || ''}`,
                })
              ),
          });
        }


        if (columns.length === 0) {
          columns.push({
            title: 'Товары',

            links: [
              {
                label:
                  'Нет товаров в этой категории',

                link: '#',
              },
            ],
          });
        }


        const adaptedData = {
          columns,

          promo: {
            image: null,

            title:
              data?.name ||
              data?.title ||
              'Все товары',

            description:
              data?.description ||
              'Перейти в каталог',

            link:
              `/catalog/${data?.slug || ''}`,
          },
        };


        setMegaMenuData(
          adaptedData
        );
      } catch (error) {
        console.error(
          'Error loading mega menu:',
          error
        );

        setMegaMenuData(null);
      }
    },
    []
  );


  useEffect(() => {
    loadMenu();
  }, [loadMenu]);


  // =========================================================
  // MEGA MENU
  // =========================================================

  const handleMouseEnter =
    useCallback(
      (menuId) => {
        if (menuTimeoutRef.current) {
          clearTimeout(
            menuTimeoutRef.current
          );

          menuTimeoutRef.current = null;
        }

        setActiveMegaMenu(menuId);

        loadMegaMenu(menuId);
      },
      [loadMegaMenu]
    );


  const handleMouseLeave =
    useCallback(() => {
      if (menuTimeoutRef.current) {
        clearTimeout(
          menuTimeoutRef.current
        );
      }

      menuTimeoutRef.current =
        setTimeout(() => {
          setActiveMegaMenu(null);

          setMegaMenuData(null);

          menuTimeoutRef.current =
            null;
        }, 400);
    }, []);


  const handleMegaMenuMouseEnter =
    useCallback((menuId) => {
      if (menuTimeoutRef.current) {
        clearTimeout(
          menuTimeoutRef.current
        );

        menuTimeoutRef.current = null;
      }

      setActiveMegaMenu(menuId);
    }, []);


  const handleMegaMenuMouseLeave =
    useCallback(() => {
      if (menuTimeoutRef.current) {
        clearTimeout(
          menuTimeoutRef.current
        );
      }

      menuTimeoutRef.current =
        setTimeout(() => {
          setActiveMegaMenu(null);

          setMegaMenuData(null);

          menuTimeoutRef.current =
            null;
        }, 400);
    }, []);


  useEffect(() => {
    return () => {
      if (menuTimeoutRef.current) {
        clearTimeout(
          menuTimeoutRef.current
        );
      }
    };
  }, []);


  // =========================================================
  // ACTIONS
  // =========================================================

  const handleLogout = () => {
    dispatch(logout());

    toast.showSuccess(
      'Вы вышли из системы'
    );

    navigate('/');

    setIsMobileMenuOpen(false);

    setActiveMegaMenu(null);
  };


  const handleSearch = (e) => {
    e.preventDefault();

    if (!searchQuery.trim()) {
      return;
    }

    navigate(
      `/catalog?search=${encodeURIComponent(
        searchQuery.trim()
      )}`
    );

    setIsMobileMenuOpen(false);
  };


  // =========================================================
  // HELPERS
  // =========================================================

  const activeMenuItem =
    activeMegaMenu
      ? menuItems.find(
          (item) =>
            item?.id === activeMegaMenu
        )
      : null;


  const hasMegaMenuData =
    megaMenuData &&
    (
      megaMenuData.columns?.length > 0 ||
      megaMenuData.promo
    );


  const getItemLabel = (item) =>
    item?.label ||
    item?.name ||
    'Без названия';


  const getItemLink = (item) =>
    item?.link ||
    `/catalog/${item?.slug || ''}`;


  const getItemHasMegaMenu = (item) =>
    item?.hasMegaMenu !== undefined
      ? item.hasMegaMenu
      : true;


  const getItemId = (item) =>
    item?.id ||
    `item-${item?.slug || Math.random()}`;


  const colCount =
    megaMenuData?.columns?.length || 1;


  const promoFlexBasis =
    `${100 / (colCount + 1)}%`;


  // =========================================================
  // HEADER
  // =========================================================

  return (
    <header
      className="
        relative
        w-full
        bg-surface
        font-[Inter,Manrope,Arial,sans-serif]
        selection:bg-accent
        selection:text-white
      "
    >

      {/* =====================================================
          GEO BAR
      ===================================================== */}

      {showGeoBar && (
        <div
          className="
            relative
            flex
            min-h-10
            items-center
            justify-center
            bg-[#17191d]
            px-10
            py-2
            text-center
            text-[11px]
            font-medium
            tracking-[.08em]
            text-white/80
          "
        >
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">

            <span>
              Вы просматриваете сайт для России.
              Для лучшего опыта рекомендуем
              выбрать ваш регион.
            </span>

            <div className="flex items-center gap-3">

              <button
                type="button"
                className="
                  flex
                  items-center
                  gap-1
                  font-bold
                  text-white
                  transition
                  hover:text-accent-hover
                "
              >
                <Globe
                  size={12}
                  strokeWidth={2}
                />

                <span>
                  Россия
                </span>

                <ChevronDown
                  size={12}
                  strokeWidth={2}
                />
              </button>

              <button
                type="button"
                className="
                  text-white
                  underline
                  underline-offset-4
                  transition
                  hover:text-accent-hover
                "
              >
                Продолжить
              </button>

            </div>
          </div>

          <button
            type="button"
            className="
              absolute
              right-4
              top-1/2
              -translate-y-1/2
              p-2
              text-white/60
              transition
              hover:text-white
            "
            onClick={() =>
              setShowGeoBar(false)
            }
          >
            <X
              size={14}
              strokeWidth={2}
            />
          </button>
        </div>
      )}


      {/* =====================================================
          LANGUAGE BAR
      ===================================================== */}

      {showLanguageBar && (
        <div
          className="
            relative
            flex
            min-h-10
            items-center
            justify-center
            border-t
            border-line/10
            bg-[#17191d]
            px-10
            py-2
            text-center
            text-[11px]
            font-medium
            tracking-[.08em]
            text-white/80
          "
        >
          <div className="flex items-center gap-4">

            <span>
              Выберите предпочтительный язык:
            </span>

            <div className="flex items-center gap-3">

              <button
                type="button"
                className="
                  font-bold
                  text-white
                  transition
                  hover:text-accent-hover
                "
              >
                Русский
              </button>

              <button
                type="button"
                className="
                  text-white/60
                  transition
                  hover:text-white
                "
              >
                English
              </button>

            </div>
          </div>

          <button
            type="button"
            className="
              absolute
              right-4
              top-1/2
              -translate-y-1/2
              p-2
              text-white/60
              transition
              hover:text-white
            "
            onClick={() =>
              setShowLanguageBar(false)
            }
          >
            <X
              size={14}
              strokeWidth={2}
            />
          </button>
        </div>
      )}


      {/* =====================================================
          UTILITY MENU
      ===================================================== */}

      <div className="border-b border-surface-2 bg-bg">

        <div
          className="
            mx-auto
            flex
            max-w-[1440px]
            justify-start
            gap-8
            px-5
            py-2
            text-[11px]
            font-normal
            text-muted
            lg:px-12
          "
        >

          <Link
            to="/corporate"
            className="transition hover:text-content"
          >
            Корпоративным клиентам
          </Link>

          <Link
            to="/partners"
            className="transition hover:text-content"
          >
            Партнерская программа
          </Link>

          <Link
            to="/academy"
            className="transition hover:text-content"
          >
            Академия
          </Link>

        </div>
      </div>


      {/* =====================================================
          DESKTOP HEADER
      ===================================================== */}

      <div className="hidden bg-surface md:block">

        <div
          className="
            relative
            mx-auto
            grid
            max-w-[1440px]
            grid-cols-[130px_minmax(0,1fr)]
            px-5
            lg:grid-cols-[130px_minmax(0,1fr)]
            lg:px-12
          "
        >

          {/* =================================================
              LOGO
          ================================================= */}

          <div className="row-span-2 flex items-center">

            <Link
              to="/"
              className="
                block
                shrink-0
                transition
                duration-300
                hover:opacity-80
              "
            >
              <img
                src={logo_hor}
                alt="Логотип"
                className="
                logo-themed
                p-2
                  h-auto
                  w-[115px]
                  object-contain
                  lg:w-[125px]
                "
              />
            </Link>

          </div>


          {/* =================================================
              TOP ROW
          ================================================= */}

          <div
            className="
              flex
              h-[64px]
              items-center
              gap-5
              pl-5
              lg:gap-6
              lg:pl-6
            "
          >

            {/* SEARCH */}

            <div className="min-w-0 flex-1">

              <form
                className="
                  group
                  relative
                  flex
                  w-full
                  items-center
                "
                onSubmit={handleSearch}
              >

                <input
                  type="text"
                  placeholder="Поиск по каталогу..."
                  value={searchQuery}
                  onChange={(e) =>
                    setSearchQuery(
                      e.target.value
                    )
                  }
                  className="
                    h-10
                    w-full
                    rounded-md
                    border
                    border-line-strong
                    bg-bg
                    pl-4
                    pr-20
                    text-sm
                    outline-none
                    transition
                    focus:border-accent-hover
                    focus:bg-surface
                    focus:ring-1
                    focus:ring-accent-hover
                  "
                />

                <div
                  className="
                    absolute
                    right-1.5
                    flex
                    items-center
                    gap-1
                  "
                >

                  {searchQuery && (
                    <button
                      type="button"
                      className="
                        p-1
                        text-subtle
                        transition
                        hover:text-content
                      "
                      onClick={() =>
                        setSearchQuery('')
                      }
                    >
                      <X
                        size={16}
                        strokeWidth={1.5}
                      />
                    </button>
                  )}

                  <button
                    type="submit"
                    className="
                      flex
                      h-7
                      w-7
                      items-center
                      justify-center
                      rounded
                      bg-accent-soft
                      text-content
                      transition
                      hover:bg-accent-hover
                      hover:text-white
                    "
                  >
                    <Search
                      size={14}
                      strokeWidth={2}
                    />
                  </button>

                </div>
              </form>

            </div>


            {/* PHONE */}

            <Link
              to="/help"
              className="
                group
                flex
                shrink-0
                items-center
                transition
              "
            >

              <Phone
                size={23}
                strokeWidth={1.2}
                className="
                  text-content
                  transition-colors
                  group-hover:text-accent-hover
                "
              />

              <div
                className="
                  grid
                  grid-cols-[0fr]
                  opacity-0
                  transition-all
                  duration-500
                  group-hover:grid-cols-[1fr]
                  group-hover:opacity-100
                "
              >
                <div className="overflow-hidden">

                  <span
                    className="
                      ml-2
                      block
                      whitespace-nowrap
                      text-[12px]
                      font-bold
                      text-accent
                    "
                  >
                    8 904 095 01 10
                  </span>

                </div>
              </div>

            </Link>


            {/* ПЕРЕКЛЮЧАТЕЛЬ ТЕМЫ — рядом с избранным и корзиной. */}
            <ThemeToggle />

            {/* FAVORITES */}

            <Link
              to="/favorites"
              className="
                group
                flex
                shrink-0
                items-center
                transition
              "
            >

              <Heart
                size={23}
                strokeWidth={1.2}
                className="
                  text-content
                  transition-colors
                  group-hover:text-accent-hover
                "
              />

              <div
                className="
                  grid
                  grid-cols-[0fr]
                  opacity-0
                  transition-all
                  duration-500
                  group-hover:grid-cols-[1fr]
                  group-hover:opacity-100
                "
              >
                <div className="overflow-hidden">

                  <span
                    className="
                      ml-2
                      block
                      whitespace-nowrap
                      text-[12px]
                      font-bold
                      text-accent
                    "
                  >
                    Избранное
                  </span>

                </div>
              </div>

            </Link>


            {/* USER */}

            {isUserAuthenticated ? (

              <button
                type="button"
                className="
                  group
                  flex
                  shrink-0
                  items-center
                  transition
                "
                onClick={handleLogout}
              >

                <User
                  size={23}
                  strokeWidth={1.2}
                  fill="currentColor"
                  className="
                    text-content
                    transition-colors
                    group-hover:text-accent-hover
                  "
                />

                <div
                  className="
                    grid
                    grid-cols-[0fr]
                    opacity-0
                    transition-all
                    duration-500
                    group-hover:grid-cols-[1fr]
                    group-hover:opacity-100
                  "
                >
                  <div className="overflow-hidden">

                    <span
                      className="
                        ml-2
                        block
                        whitespace-nowrap
                        text-[12px]
                        font-bold
                        text-accent
                      "
                    >
                      {user?.first_name ||
                        user?.login ||
                        'Профиль'}
                    </span>

                  </div>
                </div>

              </button>

            ) : (

              <button
                type="button"
                // 🔑 ВХОД — МОДАЛКА, А НЕ ПЕРЕХОД НА СТРАНИЦУ (2026-10-29).
                // Владелец: регистрация должна быть «в модальных окнах на
                // тех же страницах, чтобы и перехода не нужно было».
                // Человек с витрины стикеров нажимал вход — и терял страницу.
                onClick={() => dispatch(openAuth({ mode: 'login' }))}
                className="
                  group
                  flex
                  shrink-0
                  items-center
                  transition
                  bg-transparent
                  border-0
                  p-0
                  cursor-pointer
                "
              >

                <User
                  size={23}
                  strokeWidth={1.2}
                  className="
                    text-content
                    transition-colors
                    group-hover:text-accent-hover
                  "
                />

                <div
                  className="
                    grid
                    grid-cols-[0fr]
                    opacity-0
                    transition-all
                    duration-500
                    group-hover:grid-cols-[1fr]
                    group-hover:opacity-100
                  "
                >
                  <div className="overflow-hidden">

                    <span
                      className="
                        ml-2
                        block
                        whitespace-nowrap
                        text-[12px]
                        font-bold
                        text-accent
                      "
                    >
                      Войти
                    </span>

                  </div>
                </div>

              </button>

            )}


            {/* CART */}

            <Link
              to="/cart"
              className="
                group
                flex
                shrink-0
                items-center
                transition
              "
            >

              <div className="relative">

                <ShoppingBag
                  size={23}
                  strokeWidth={1.2}
                  className="
                    text-content
                    transition-colors
                    group-hover:text-accent-hover
                  "
                />

                <span
                  className="
                    absolute
                    -right-2
                    -top-1.5
                    flex
                    h-4
                    w-4
                    items-center
                    justify-center
                    rounded-full
                    border
                    border-line
                    bg-accent
                    text-[9px]
                    font-bold
                    text-white
                  "
                >
                  2
                </span>

              </div>

              <div
                className="
                  grid
                  grid-cols-[0fr]
                  opacity-0
                  transition-all
                  duration-500
                  group-hover:grid-cols-[1fr]
                  group-hover:opacity-100
                "
              >
                <div className="overflow-hidden">

                  <span
                    className="
                      ml-2
                      block
                      whitespace-nowrap
                      text-[12px]
                      font-bold
                      text-accent
                    "
                  >
                    Корзина
                  </span>

                </div>
              </div>

            </Link>

          </div>


          {/* =================================================
              BOTTOM API MENU
          ================================================= */}

          <div className="min-w-0 pl-5 lg:pl-6">

            <nav className="w-full">

              <ul className="flex w-full items-center justify-between">

                {isLoading ? (

                  Array.from({ length: 5 }).map(
                    (_, i) => (
                      <li
                        key={i}
                        className="
                          py-3
                          text-[13px]
                          font-medium
                          text-content
                          opacity-30
                        "
                      >
                        Загрузка...
                      </li>
                    )
                  )

                ) : (

                  menuItems.map((item) => {

                    const label =
                      getItemLabel(item);

                    const link =
                      getItemLink(item);

                    const hasMegaMenu =
                      getItemHasMegaMenu(item);

                    const itemId =
                      getItemId(item);


                    return (
                      <li
                        key={itemId}
                        className="
                          group
                          relative
                        "
                        onMouseEnter={() =>
                          hasMegaMenu &&
                          handleMouseEnter(
                            itemId
                          )
                        }
                        onMouseLeave={
                          hasMegaMenu
                            ? handleMouseLeave
                            : undefined
                        }
                      >

                        <Link
                          to={link}
                          className={`
                            block
                            px-2
                            pb-3
                            text-[13px]
                            font-medium
                            transition-colors
                            duration-300
                            ${
                              activeMegaMenu ===
                              itemId
                                ? 'text-accent'
                                : 'text-content hover:text-accent-hover'
                            }
                          `}
                        >
                          {label.replace(
                            '\n',
                            ' '
                          )}
                        </Link>


                        <div
                          className={`
                            absolute
                            bottom-0
                            left-0
                            h-[2px]
                            bg-accent
                            transition-all
                            duration-500
                            ${
                              activeMegaMenu ===
                              itemId
                                ? 'w-full opacity-100'
                                : 'w-0 opacity-0'
                            }
                          `}
                        />

                      </li>
                    );
                  })

                )}

              </ul>

            </nav>

          </div>


          {/* =================================================
              MEGA MENU
          ================================================= */}

          <div
            className={`
              absolute
              left-0
              top-full
              z-40
              w-full
              origin-top
              overflow-hidden
              border-t
              border-line-strong
              bg-surface
              shadow-[0_10px_40px_rgba(0,0,0,0.08)]
              transition-all
              duration-500
              ${
                activeMenuItem &&
                hasMegaMenuData
                  ? 'pointer-events-auto scale-y-100 opacity-100'
                  : 'pointer-events-none scale-y-0 opacity-0'
              }
            `}
            onMouseEnter={() =>
              activeMegaMenu &&
              handleMegaMenuMouseEnter(
                activeMegaMenu
              )
            }
            onMouseLeave={
              handleMegaMenuMouseLeave
            }
          >

            {megaMenuData && (

              <div
                className="
                  mx-auto
                  flex
                  max-w-[1440px]
                  justify-between
                  gap-10
                  px-5
                  py-8
                  lg:px-12
                "
              >

                <div
                  className="
                    grid
                    min-w-0
                    flex-1
                    gap-8
                  "
                  style={{
                    gridTemplateColumns:
                      `repeat(${colCount}, minmax(0, 1fr))`,
                  }}
                >

                  {megaMenuData.columns?.map(
                    (column, index) => (

                      <div
                        key={index}
                        className="min-w-0"
                      >

                        <h3
                          className="
                            mb-4
                            text-[13px]
                            font-bold
                            text-content
                          "
                        >
                          {column?.title ||
                            'Категория'}
                        </h3>


                        <ul
                          className="
                            space-y-2.5
                            text-[13px]
                            font-normal
                            text-content
                          "
                        >

                          {column?.links?.map(
                            (link, li) => (

                              <li key={li}>

                                <Link
                                  to={
                                    link?.link ||
                                    '#'
                                  }
                                  className="
                                    inline-block
                                    transition-all
                                    duration-300
                                    hover:text-accent-hover
                                    hover:underline
                                    hover:underline-offset-4
                                  "
                                >
                                  {link?.label ||
                                    'Товар'}
                                </Link>

                              </li>

                            )
                          )}

                        </ul>

                      </div>

                    )
                  )}

                </div>


                {/* PROMO */}

                {megaMenuData.promo && (

                  <div
                    className="
                      shrink-0
                      border-l
                      border-surface-2
                      pl-8
                    "
                    style={{
                      flexBasis:
                        promoFlexBasis,

                      maxWidth:
                        '280px',
                    }}
                  >

                    <div
                      className="
                        aspect-[4/3]
                        w-full
                        overflow-hidden
                        rounded-lg
                        bg-accent-soft
                      "
                    >

                      <img
                        src={
                          megaMenuData.promo
                            .image ||
                          PLACEHOLDER_IMAGE
                        }
                        alt={
                          megaMenuData.promo
                            .title ||
                          'Промо'
                        }
                        className="
                          h-full
                          w-full
                          object-cover
                          mix-blend-multiply
                          transition-all
                          duration-700
                          hover:scale-105
                        "
                        onError={(e) => {
                          e.target.src =
                            PLACEHOLDER_IMAGE;
                        }}
                      />

                    </div>


                    <div className="pt-3">

                      <div
                        className="
                          text-[14px]
                          font-bold
                          text-content
                        "
                      >
                        {megaMenuData.promo
                          .title ||
                          'Все товары'}
                      </div>


                      <Link
                        to={
                          megaMenuData.promo
                            .link ||
                          '/catalog'
                        }
                        className="
                          mt-2
                          inline-flex
                          items-center
                          gap-1.5
                          text-[12px]
                          font-medium
                          text-accent
                          transition-all
                          duration-300
                          hover:gap-2
                          hover:text-accent-hover
                        "
                      >
                        Подробнее

                        <ArrowRight
                          size={12}
                          strokeWidth={2}
                        />
                      </Link>

                    </div>

                  </div>

                )}

              </div>

            )}

          </div>

        </div>

      </div>


      {/* =====================================================
          MOBILE HEADER
      ===================================================== */}

      <div
        className="
          flex
          items-center
          justify-between
          border-b
          border-surface-2
          bg-surface
          px-5
          py-4
          md:hidden
        "
      >

        <button
          type="button"
          className="
            flex
            h-10
            w-10
            items-center
            justify-center
            text-content
          "
          onClick={() =>
            setIsMobileMenuOpen(true)
          }
          aria-label="Открыть меню"
        >
          <Menu
            size={25}
            strokeWidth={1.5}
          />
        </button>


        <Link
          to="/"
          className="transition hover:opacity-80"
        >
          <img
            src={logo_hor}
            alt="Логотип"
            className="
              logo-themed
              h-10
              w-auto
              object-contain
            "
          />
        </Link>


        <Link
          to="/cart"
          className="
            relative
            flex
            h-10
            w-10
            items-center
            justify-center
            text-content
          "
        >

          <ShoppingBag
            size={24}
            strokeWidth={1.3}
          />

          <span
            className="
              absolute
              right-0.5
              top-0.5
              flex
              h-4
              w-4
              items-center
              justify-center
              rounded-full
              bg-accent
              text-[9px]
              font-bold
              text-white
            "
          >
            2
          </span>

        </Link>

      </div>


      {/* =====================================================
          PROMO BAR
          
          IMPORTANT:
          NO z-index HERE
      ===================================================== */}

      {showPromoBar && (

        <div
          className="
            bg-accent
            px-4
            py-2.5
            text-center
            text-xs
            font-bold
            text-white
            transition-all
            duration-500
            hover:bg-accent-hover
          "
        >

          {/* ⚠️ БЫЛО `to="/offers"` — маршрута `/offers` в routes.jsx НЕТ,
              то есть кнопка вела на страницу 404. Исправлено 2026-10-07.
              По смыслу надписи («Изучите наши последние предложения») это
              АКЦИИ, а не юридическая оферта: у нас есть `/deals` → «Акции». */}
          <Link
            to="/deals"
            className="
              flex
              items-center
              justify-center
              gap-3
              text-white
            "
          >

            <span>
              Изучите наши последние предложения

              <span
                className="
                  mx-2
                  font-normal
                  text-white/50
                "
              >
                |
              </span>

              Изготовим и согласуем макет
              до оплаты
            </span>


            <ArrowRight
              size={14}
              strokeWidth={2}
              className="
                transition-transform
                duration-300
                group-hover:translate-x-1
              "
            />

          </Link>

        </div>

      )}


      {/* =====================================================
          MOBILE MENU

          z-[9999] — перекрывает PROMO BAR
      ===================================================== */}

      <div
        className={`
          fixed
          inset-0
          z-[9999]
          flex
          flex-col
          bg-surface
          transition-all
          duration-500
          ease-out
          md:hidden
          ${
            isMobileMenuOpen
              ? 'pointer-events-auto translate-x-0 opacity-100'
              : 'pointer-events-none translate-x-full opacity-0'
          }
        `}
      >

        {/* MOBILE TOP */}

        <div
          className="
            flex
            shrink-0
            items-center
            justify-between
            border-b
            border-surface-2
            px-5
            py-4
          "
        >

          <Link
            to="/"
            onClick={() =>
              setIsMobileMenuOpen(false)
            }
          >
            <img
              src={logo_hor}
              alt="Логотип"
              className="
                logo-themed
                h-10
                w-auto
                object-contain
              "
            />
          </Link>


          <button
            type="button"
            className="
              flex
              h-10
              w-10
              items-center
              justify-center
              text-content
              transition
              hover:opacity-60
            "
            onClick={() =>
              setIsMobileMenuOpen(false)
            }
            aria-label="Закрыть меню"
          >
            <X
              size={27}
              strokeWidth={1.5}
            />
          </button>

        </div>


        {/* MOBILE CONTENT */}

        <div
          className="
            flex-1
            overflow-y-auto
            px-5
            py-6
          "
        >

          {/* SEARCH */}

          <form
            className="
              relative
              mb-8
              flex
              items-center
            "
            onSubmit={handleSearch}
          >

            <Search
              size={18}
              strokeWidth={1.5}
              className="
                absolute
                left-4
                text-subtle
              "
            />

            <input
              type="text"
              placeholder="Поиск по каталогу..."
              value={searchQuery}
              onChange={(e) =>
                setSearchQuery(
                  e.target.value
                )
              }
              className="
                h-12
                w-full
                rounded-md
                border
                border-line-strong
                bg-bg
                pl-12
                pr-4
                text-sm
                outline-none
                transition
                focus:border-accent-hover
                focus:bg-surface
              "
            />

          </form>


          {/* API MENU */}

          <nav className="flex flex-col">

            {menuItems.map((item) => (

              <Link
                key={getItemId(item)}
                to={getItemLink(item)}
                className="
                  flex
                  items-center
                  justify-between
                  border-b
                  border-surface-2
                  py-5
                  text-[19px]
                  font-medium
                  text-content
                  transition-all
                  duration-300
                  hover:pl-2
                  hover:text-accent-hover
                "
                onClick={() =>
                  setIsMobileMenuOpen(false)
                }
              >

                <span>
                  {getItemLabel(item).replace(
                    '\n',
                    ' '
                  )}
                </span>

                <ArrowRight
                  size={18}
                  strokeWidth={1.5}
                  className="text-subtle"
                />

              </Link>

            ))}

          </nav>


          {/* MOBILE LINKS */}

          <div
            className="
              mt-8
              flex
              flex-col
              border-t
              border-surface-2
              pt-6
            "
          >

            <Link
              to="/corporate"
              onClick={() =>
                setIsMobileMenuOpen(false)
              }
              className="
                py-3
                text-sm
                text-muted
              "
            >
              Корпоративным клиентам
            </Link>

            <Link
              to="/partners"
              onClick={() =>
                setIsMobileMenuOpen(false)
              }
              className="
                py-3
                text-sm
                text-muted
              "
            >
              Партнерская программа
            </Link>

            <Link
              to="/academy"
              onClick={() =>
                setIsMobileMenuOpen(false)
              }
              className="
                py-3
                text-sm
                text-muted
              "
            >
              Академия
            </Link>

            <Link
              to="/favorites"
              onClick={() =>
                setIsMobileMenuOpen(false)
              }
              className="
                py-3
                text-sm
                text-muted
              "
            >
              Избранное
            </Link>

            <Link
              to="/help"
              onClick={() =>
                setIsMobileMenuOpen(false)
              }
              className="
                py-3
                text-sm
                text-muted
              "
            >
              Помощь и контакты
            </Link>

          </div>


          {/* AUTH */}

          <div
            className="
              mt-6
              border-t
              border-surface-2
              pt-6
            "
          >

            {isUserAuthenticated ? (

              <div className="flex flex-col gap-4">

                <div
                  className="
                    text-sm
                    font-bold
                    text-content
                  "
                >
                  {user?.first_name ||
                    user?.login ||
                    'Профиль'}
                </div>

                <button
                  type="button"
                  onClick={handleLogout}
                  className="
                    w-full
                    rounded-md
                    border
                    border-line-strong
                    bg-surface
                    py-3.5
                    text-sm
                    font-bold
                    text-content
                    transition
                    hover:border-[#17191d]
                    hover:bg-surface-2
                  "
                >
                  Выйти
                </button>

              </div>

            ) : (

              <button
                type="button"
                // ⚠️ КЛАСС `bg-accent` НЕ ГЕНЕРИРУЕТСЯ сборкой (грабля 10-БИС).
                // Кнопка была бы БЕЛОЙ НА БЕЛОМ. Цвет — инлайном из токена.
                // ⚠️ Меню закрываем ДО открытия модалки: иначе модалка
                // окажется под выехавшим меню и будет недоступна.
                style={{ backgroundColor: 'rgb(var(--c-accent))' }}
                className="
                  flex
                  w-full
                  items-center
                  justify-center
                  rounded-md
                  py-3.5
                  text-sm
                  font-bold
                  text-white
                  transition
                  hover:scale-[1.01]
                  border-0
                  cursor-pointer
                "
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  dispatch(openAuth({ mode: 'login' }));
                }}
              >
                Войти в профиль
              </button>

            )}

          </div>

        </div>

      </div>

    </header>
  );
};


export default Header;